import React from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const STUDIO_UPDATE_AGENT_DRAFT_INTERACTION_SOURCE = 'studioUpdateAgentDraftRenderer';

/*
 * This action will eventually be replaced by the StudioWorkspaceUpdateAgentDraftAction.
 * It is being kept temporarily to ensure backward compatibility with existing
 * integrations that may still be using this action key.
 */
export const StudioUpdateAgentDraftAction = ({
	actions,
	getCancelButton,
	getStatus,
	recipientAgentId,
	conversationId,
	messageId,
	onCustomDataConfirm,
	sendMessageConfirmedTools,
}: ActionRendererFor<typeof ActionKeys.StudioUpdateAgentDraft>): React.JSX.Element => {
	di(usePublish);

	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');

	const firstAction = actions[0];
	const { data } = firstAction;
	const status = getStatus(firstAction);

	const { buildId, draftContent } = data;

	const isDisabled = status !== 'idle';
	const isSubmitting = status === 'started';

	const onClick = async () => {
		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');

			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: STUDIO_UPDATE_AGENT_DRAFT_INTERACTION_SOURCE,
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', data)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
		} else {
			await onCustomDataConfirm(firstAction, data);
		}

		publish({
			type: 'solution-draft-agent-update',
			data: {
				buildId,
				draftContent,
			},
			source: 'update-agent-draft-action-ui',
		});
	};
	return (
		<Inline space="space.150">
			{getCancelButton(firstAction, isDisabled)}

			<Button
				appearance="primary"
				isLoading={isSubmitting}
				isDisabled={isDisabled}
				onClick={onClick}
			>
				{formatMessage(messages.confirmButton)}
			</Button>
		</Inline>
	);
};
